import { computed, ref, toValue, watch } from "vue";
import { useRafFn } from "@vueuse/core";
const DEFAULT_INITIAL_BUDGET = 40;
// Segments per frame. Tuned on an 842-segment transcript: larger chunks finish sooner but block
// longer (100 → a 425ms task), smaller ones smooth out at the cost of total fill time.
const DEFAULT_CHUNK_WEIGHT = 25;
/**
 * Renders a long list as a growing prefix: a small first paint, then a frame-driven fill until the
 * whole list is mounted. Weight is caller-defined, so a chunk boundary can cut *inside* one entry
 * rather than only between entries.
 */
export const useProgressiveReveal = (items, opts) => {
    const initialBudget = Math.max(1, opts.initialBudget ?? DEFAULT_INITIAL_BUDGET);
    const chunkWeight = Math.max(1, opts.chunkWeight ?? DEFAULT_CHUNK_WEIGHT);
    const list = () => toValue(items);
    const isEnabled = () => toValue(opts.enabled ?? true);
    // offsets[i] is entry i's start; the final element is the total.
    const offsets = computed(() => {
        const result = [0];
        for (const item of list())
            result.push(result[result.length - 1] + Math.max(1, opts.weightOf(item)));
        return result;
    });
    const totalWeight = computed(() => offsets.value[offsets.value.length - 1]);
    const limit = ref(initialBudget);
    const revealed = computed(() => {
        const all = list();
        if (!isEnabled())
            return { count: all.length, tailCap: undefined };
        const capped = Math.min(limit.value, totalWeight.value);
        let count = 0;
        let tailCap;
        for (let index = 0; index < all.length; index++) {
            const start = offsets.value[index];
            if (start >= capped)
                break;
            count++;
            if (offsets.value[index + 1] > capped)
                tailCap = capped - start;
        }
        // Always show something, so a single heavy entry is never a blank screen.
        if (count === 0 && all.length > 0)
            return { count: 1, tailCap: Math.min(initialBudget, offsets.value[1]) };
        return { count, tailCap };
    });
    const visibleItems = computed(() => list().slice(0, revealed.value.count));
    const tailCap = computed(() => revealed.value.tailCap);
    const isComplete = computed(() => !isEnabled() || limit.value >= totalWeight.value);
    const { pause, resume } = useRafFn(() => {
        limit.value = Math.min(limit.value + chunkWeight, totalWeight.value);
        if (limit.value >= totalWeight.value)
            pause();
    }, { immediate: false });
    // Covers the async transcript load and live appends: whenever there is more to draw, keep drawing.
    watch([isComplete, () => isEnabled()], ([complete, enabled]) => {
        if (enabled && !complete)
            resume();
        else
            pause();
    }, { immediate: true, flush: "post" });
    // revealThrough takes a weight, so a caller needs these offsets to name a position inside the list.
    const offsetAt = (index) => offsets.value[index];
    const revealThrough = (weight) => {
        if (!isEnabled())
            return;
        limit.value = Math.max(limit.value, Math.min(weight + 1, totalWeight.value));
    };
    const revealAll = () => {
        limit.value = totalWeight.value;
    };
    const reset = () => {
        limit.value = Math.min(initialBudget, totalWeight.value);
    };
    let prevLength = 0;
    let prevFirstKey = null;
    let prevTailKey = null;
    // A filter change invalidates the prefix; an append does not. Comparing both ends of the previous
    // list is an O(1) stand-in for a diff — a filter moves the length or the first entry.
    watch(() => list(), (next) => {
        const isAppend = next.length >= prevLength &&
            (prevLength === 0 ||
                (opts.keyOf(next[0], 0) === prevFirstKey &&
                    opts.keyOf(next[prevLength - 1], prevLength - 1) === prevTailKey));
        prevLength = next.length;
        prevFirstKey = next.length ? opts.keyOf(next[0], 0) : null;
        prevTailKey = next.length ? opts.keyOf(next[next.length - 1], next.length - 1) : null;
        if (!isAppend)
            reset();
    }, { immediate: true, flush: "post" });
    return { visibleItems, tailCap, isComplete, offsetAt, revealThrough, revealAll };
};
